<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-primary md-round" @click="goToPayment">
        <md-icon>add</md-icon>
        Payment List
      </md-button>
    </template>
    <template #content>

    <div class="filters-bar">
      <div class="md-layout md-gutter">
        <div class="md-layout-item md-size-20 md-small-size-100">
          <md-field>
            <label>Start Date</label>
            <md-input type="date" v-model="filters.start_date" @change="fetchReadyLabourers()"/>
          </md-field>
        </div>
        <div class="md-layout-item md-size-20">
          <md-field>
            <label>End Date</label>
            <md-input type="date" v-model="filters.end_date" @change="fetchReadyLabourers()"/>
          </md-field>
        </div>
      </div>
    </div>

    <!-- Labourers Ready for Payment -->
      <table-data
          ref="table"
          title="Ready For Payments"
          :columns="columns"
          :data="readyLabourers"
          :total="labourersMeta.total"
          :loading="loadingLabourers"
          :searchable="true"
          search-placeholder="Search by payment number or labourer..."
          storage-key="labourer-payments-list"
          empty-message="No labourer ready for payments found. Making sure supervisor checlout marked"
          @fetch="onTableFetch"
      >
        <template #cell-labourer_name="{ item }">
          <clickable-link
              :id="item.id"
              resource="Labourer"
              :text="item.name"
              :stop-propagation="true"
          />
          <br/><badge type="info">{{item.nida_number}}</badge>
        </template>
        <template #cell-schedule="{ item }">
          {{ item.schedules_count }}
        </template>
        <template #cell-hours="{ item }">
          {{ item.total_hours }}
        </template>
        <template #cell-total_amount="{ item }">
          {{ formatCurrency(item.total_amount) }}
        </template>
        <template #actions="{ item }">
          <md-button
              class="md-icon-button md-raised md-round md-info"
              @click="openLabourerSchedules(item)">
            <md-icon>monetization_on</md-icon>
          </md-button>
        </template>
      </table-data>

    <!-- Labourer Schedules Modal -->
    <md-dialog :md-active.sync="modal.schedules.active" class="schedules-modal">
      <md-dialog-title>
        <div class="modal-title">
          <span>Schedules for {{ selectedLabourer ? selectedLabourer.name : '' }}</span>
          <span class="subtitle">{{ selectedLabourer ? selectedLabourer.nida_number : '' }}</span>
        </div>
      </md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <div class="summary-bar">
            <div class="summary-item">
              <span class="label">Total Schedules:</span>
              <span class="value">{{ labourerSchedules.length }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Selected:</span>
              <span class="value">{{ selectedSchedules.length }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Total Hours:</span>
              <span class="value">{{ totalSelectedHours }}</span>
            </div>
            <div class="summary-item">
              <span class="label">Total Amount:</span>
              <span class="value">TZS {{ formatNumber(totalSelectedAmount) }}</span>
            </div>
          </div>

          <div class="table-wrapper">
            <md-table>
              <md-table-row>
                <md-table-head>
                  <md-checkbox
                      :value="allSelected"
                      @change="toggleAllSchedules"
                  />
                </md-table-head>
                <md-table-head>Takeon</md-table-head>
                <md-table-head>Shift</md-table-head>
                <md-table-head>Workplace</md-table-head>
                <md-table-head>Check In</md-table-head>
                <md-table-head>Check Out</md-table-head>
                <md-table-head class="text-right">Hours</md-table-head>
                <md-table-head class="text-right">Rate</md-table-head>
                <md-table-head class="text-right">Amount</md-table-head>
              </md-table-row>
              <md-table-row v-for="schedule in labourerSchedules" :key="schedule.id">
                <md-table-cell>
                  <md-checkbox v-model="selectedSchedules" :value="schedule"/>
                </md-table-cell>
                <md-table-cell>{{schedule.takeon_number}}<br/><badge type="info">{{ formatDate(schedule.shift_date) }}</badge></md-table-cell>
                <md-table-cell>{{ schedule.shift_name }}</md-table-cell>
                <md-table-cell>{{ schedule.workplace_name }}</md-table-cell>
                <md-table-cell>{{ formatTime(schedule.checkin_at) }}</md-table-cell>
                <md-table-cell>{{ formatTime(schedule.checkout_at) }}</md-table-cell>
                <md-table-cell class="text-right">{{ schedule.hours_worked }}</md-table-cell>
                <md-table-cell class="text-right">TZS {{ formatNumber(schedule.rate_per_hour) }}</md-table-cell>
                <md-table-cell class="text-right">TZS {{ formatNumber(schedule.amount) }}</md-table-cell>
              </md-table-row>
            </md-table>
          </div>

          <div class="notes-section">
            <md-field>
              <label>Payment Notes (Optional)</label>
              <md-textarea v-model="paymentNotes" rows="2"/>
            </md-field>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="createPayment" :disabled="selectedSchedules.length === 0">
          <md-icon>payment</md-icon>
          Create Payment ({{ selectedSchedules.length }} items)
        </md-button>
        <md-button class="md-danger" @click="closeSchedulesModal">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- View Payment Modal -->
    <md-dialog :md-active.sync="modal.payment.active" class="payment-modal">
      <md-dialog-title>Payment Details - {{ selectedPayment ? selectedPayment.payment_number : '' }}</md-dialog-title>
      <md-dialog-content>
        <div class="payment-details" v-if="selectedPayment">
          <div class="payment-header">
            <div class="info-group">
              <div><strong>Labourer:</strong> {{ selectedPayment.labourer ? selectedPayment.labourer.name : '' }}</div>
              <div><strong>Date:</strong> {{ formatDate(selectedPayment.payment_date) }}</div>
              <div><strong>Period:</strong> {{ formatDate(selectedPayment.start_date) }} -
                {{ formatDate(selectedPayment.end_date) }}
              </div>
            </div>
            <div class="info-group">
              <div><strong>Status:</strong>
                <badge :type="getStatusBadge(selectedPayment.status)">{{ selectedPayment.status }}</badge>
              </div>
              <div><strong>Items:</strong> {{ selectedPayment.total_items }}</div>
              <div><strong>Total Amount:</strong> TZS {{ formatNumber(selectedPayment.total_amount) }}</div>
            </div>
          </div>

          <div class="table-wrapper">
            <md-table>
              <md-table-row>
                <md-table-head>#</md-table-head>
                <md-table-head>Date</md-table-head>
                <md-table-head>Shift</md-table-head>
                <md-table-head>Check In</md-table-head>
                <md-table-head>Check Out</md-table-head>
                <md-table-head class="text-right">Hours</md-table-head>
                <md-table-head class="text-right">Rate</md-table-head>
                <md-table-head class="text-right">Amount</md-table-head>
              </md-table-row>
              <md-table-row v-for="(item, index) in selectedPayment.items" :key="item.id">
                <md-table-cell>{{ index + 1 }}</md-table-cell>
                <md-table-cell>{{ formatDate(item.shift_date) }}</md-table-cell>
                <md-table-cell>{{
                    item.schedule ? item.schedule.shift ? item.schedule.shift.name : 'N/A' : 'N/A'
                  }}
                </md-table-cell>
                <md-table-cell>{{ formatTime(item.start_time) }}</md-table-cell>
                <md-table-cell>{{ formatTime(item.end_time) }}</md-table-cell>
                <md-table-cell class="text-right">{{ item.hours_worked }}</md-table-cell>
                <md-table-cell class="text-right">TZS {{ formatNumber(item.rate_per_hour) }}</md-table-cell>
                <md-table-cell class="text-right">TZS {{ formatNumber(item.amount) }}</md-table-cell>
              </md-table-row>
            </md-table>
          </div>

          <div v-if="selectedPayment.notes" class="notes-display">
            <strong>Notes:</strong> {{ selectedPayment.notes }}
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="closePaymentModal">Close</md-button>
        <md-button
            v-if="selectedPayment && selectedPayment.status === 'draft'"
            class="md-success"
            @click="approvePayment(selectedPayment)"
        >
          <md-icon>check_circle</md-icon>
          Approve
        </md-button>
        <md-button
            v-if="selectedPayment && selectedPayment.status === 'approved'"
            class="md-info"
            @click="markAsPaid(selectedPayment)"
        >
          <md-icon>payment</md-icon>
          Mark as Paid
        </md-button>
      </md-dialog-actions>
    </md-dialog>
    </template>
  </page-layout>
</template>

<script>
import {mapActions} from 'vuex';
import Badge from '@/components/Badge.vue';
import _ from 'lodash';
import PageLayout from "@/components/Layouts/PageLayout.vue";
import TableData from "@/components/DataTable/TableData.vue";

export default {
  name: 'LabourerPayment',
  components: {Badge,TableData,
    PageLayout: PageLayout,},
  data() {
    return {
      filters: {
        start_date: this.getDefaultStartDate(),
        end_date: this.getDefaultEndDate(),
        search: '',
        status: ''
      },
      readyLabourers: [],
      labourerSchedules: [],
      selectedSchedules: [],
      payments: [],
      selectedLabourer: null,
      selectedPayment: null,
      paymentNotes: '',
      loadingLabourers: false,
      loadingPayments: false,
      labourersView: 'table',
      paymentsPerPage: 15,
      labourersMeta: {current_page: 1, last_page: 1, per_page: 15, total: 0, from: 0, to: 0},
      paymentsMeta: {current_page: 1, last_page: 1, per_page: 15, total: 0, from: 0, to: 0},

      modal: {
        schedules: {active: false},
        payment: {active: false}
      },

      columns: [
        { field: 'labourer_name', label: 'Labourer Name' },
        { field: 'schedule', label: 'Schedule Count' },
        { field: 'hours', label: 'Hours' },
        { field: 'total_amount', label: 'Amount', align: 'text-right' }
      ],
      tableState: {
        page: 1,
        perPage: 15,
        search: ''
      }
    };
  },
  computed: {
    pageTitle: function () { return 'Labourer Payments'; },
    pageBreadcrumb: function () {
      return [
        { label: 'Dashboard', path: '/' },
        { label: 'Labourer Payment' ,path:'/labourer/payment/list'},
        { label: 'Ready For Paymnet' },
      ];
    },
    pageInfoCards: function () {
      var totalLabourer = 0;
      var totalSchedule = 0;
      var totalHours = 0;
      var totalAmount = 0;

      // this.paymentsList.forEach(function (item) {
      //   if (item.current_status === 'draft') draftCount++;
      //   else if (item.current_status === 'approved') approvedCount++;
      //   else if (item.current_status === 'paid') paidCount++;
      //   totalAmount += parseFloat(item.total_amount || 0);
      // });

      return [
        { icon: 'users', label: 'Labourers', value: totalLabourer, color: 'warning' },
        { icon: 'calendar', label: 'Schedule', value: totalSchedule, color: 'info' },
        { icon: 'clock', label: 'Hours', value: totalHours, color: 'success' },
        { icon: 'attach_money', label: 'Total Amount', value: this.formatCurrency(totalAmount), color: 'primary' }
      ];
    },

    allSelected() {
      return this.labourerSchedules.length > 0 &&
          this.selectedSchedules.length === this.labourerSchedules.length;
    },

    totalSelectedHours() {
      return this.selectedSchedules.reduce((sum, s) => sum + (s.hours_worked || 0), 0);
    },

    totalSelectedAmount() {
      return this.selectedSchedules.reduce((sum, s) => sum + (s.amount || 0), 0);
    }
  },
  created() {
    this.fetchReadyLabourers();
  },
  methods: {
    ...mapActions('labourerPayments', [
      'fetchReadyLabourersAction',
      'fetchLabourerSchedulesAction',
      'createPaymentAction',
      'approvePaymentAction',
      'markAsPaidAction',
      'list'
    ]),

    getDefaultStartDate() {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return this.formatDateYMD(date);
    },

    getDefaultEndDate() {
      return this.formatDateYMD(new Date());
    },

    async onTableFetch(state) {
      this.filters.search = state.search
      this.filters.per_page = state.perPage
      this.filters.page = state.page
      await this.fetchReadyLabourers();
    },

    async fetchReadyLabourers() {

      this.loadingLabourers = true;
      try {
        await this.fetchReadyLabourersAction({
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          search: this.filters.search,
          workplace_id: this.filters.workplace_id || undefined,
          page: this.filters.page,
          per_page: this.filters.per_page,
        });
        this.readyLabourers = this.$store.getters['labourerPayments/list'];
        this.labourersMeta = this.$store.getters['labourerPayments/meta'];
      } catch (error) {
        console.log('error error error');
        console.log(error);
        this.notifyException(error);
        this.notifyError('Failed to fetch labourers');
      } finally {
        this.loadingLabourers = false;
      }
    },


    async openLabourerSchedules(labourer) {
      this.selectedLabourer = labourer;
      this.selectedSchedules = [];
      this.paymentNotes = '';

      try {
        const response = await this.fetchLabourerSchedulesAction({
          labourerId: labourer.id,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date
        });
        this.labourerSchedules = response.data || [];
        this.modal.schedules.active = true;
      } catch (error) {
        this.notifyError('Failed to fetch labourer schedules');
      }
    },

    toggleAllSchedules() {
      if (this.allSelected) {
        this.selectedSchedules = [];
      } else {
        this.selectedSchedules = [...this.labourerSchedules];
      }
    },

    async createPayment() {
      if (this.selectedSchedules.length === 0) {
        this.notifyWarning('Please select at least one schedule');
        return;
      }

      try {
        const scheduleIds = this.selectedSchedules.map(s => s.id);
        const response = await this.createPaymentAction({
          labourer_id: this.selectedLabourer.id,
          schedule_ids: scheduleIds,
          notes: this.paymentNotes
        });

        this.notifySuccess('Payment created successfully');
        this.closeSchedulesModal();
        await this.fetchReadyLabourers();
      } catch (error) {
        this.notifyError('Failed to create payment');
      }
    },

    closeSchedulesModal() {
      this.modal.schedules.active = false;
      this.selectedLabourer = null;
      this.labourerSchedules = [];
      this.selectedSchedules = [];
    },

    async viewPayment(payment) {
      this.selectedPayment = payment;
      this.modal.payment.active = true;
    },

    async approvePayment(payment) {
      try {
        await this.approvePaymentAction(payment.id);
        this.notifySuccess('Payment approved successfully');
        if (this.modal.payment.active) {
          this.selectedPayment = {...this.selectedPayment, status: 'approved'};
        }
      } catch (error) {
        this.notifyError('Failed to approve payment');
      }
    },

    async markAsPaid(payment) {
      try {
        await this.markAsPaidAction(payment.id);
        this.notifySuccess('Payment marked as paid successfully');
        if (this.modal.payment.active) {
          this.selectedPayment = {...this.selectedPayment, status: 'paid'};
        }
      } catch (error) {
        this.notifyError('Failed to mark payment as paid');
      }
    },

    closePaymentModal() {
      this.modal.payment.active = false;
      this.selectedPayment = null;
    },

    getStatusBadge(status) {
      const badges = {
        'draft': 'warning',
        'approved': 'info',
        'paid': 'success',
        'cancelled': 'danger'
      };
      return badges[status] || 'default';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString();
    },

    formatDateYMD(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formatTime(time) {
      if (!time) return '--:--';
      const d = new Date(time);
      return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    },

    formatNumber(number) {
      if (!number) return '0';
      return new Intl.NumberFormat().format(number);
    },
    goToLabourersPage(page) {
      if (page < 1 || page > this.labourersMeta.last_page) return;
      this.fetchReadyLabourers(page);
    },


    goToPayment(){
        this.$router.push({ name: 'Labourer Payment List' });
    },

    formatCurrency(value) {
      if (!value) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
  }
};
</script>

<style scoped>
.labourer-payment {
  padding: 20px;
}

.filters-bar {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.labourers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.labourer-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.labourer-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.labourer-info {
  flex: 1;
}

.labourer-name {
  font-weight: bold;
  font-size: 14px;
}

.labourer-code {
  font-size: 12px;
  color: #666;
}

.labourer-stats {
  display: flex;
  gap: 15px;
  margin: 0 15px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 10px;
  color: #999;
}

.stat-value {
  display: block;
  font-size: 14px;
  font-weight: bold;
}

.select-btn {
  min-width: 40px !important;
}

.payments-table-wrapper {
  overflow-x: auto;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-state md-icon {
  font-size: 48px;
  width: 48px;
  height: 48px;
  margin-bottom: 10px;
}

.schedules-modal .md-dialog {
  max-width: 900px;
  width: 95%;
}

.modal-title {
  display: flex;
  flex-direction: column;
}

.modal-title .subtitle {
  font-size: 12px;
  color: #666;
  font-weight: normal;
}

.summary-bar {
  display: flex;
  gap: 20px;
  padding: 10px 15px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 15px;
}

.summary-item .label {
  font-size: 12px;
  color: #666;
  margin-right: 5px;
}

.summary-item .value {
  font-weight: bold;
  font-size: 14px;
}

.notes-section {
  margin-top: 15px;
}

.payment-modal .md-dialog {
  max-width: 800px;
  width: 95%;
}

.payment-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 15px;
}

.info-group div {
  padding: 3px 0;
  font-size: 13px;
}

.notes-display {
  margin-top: 15px;
  padding: 10px;
  background: #fafafa;
  border-radius: 4px;
  font-size: 13px;
}

.text-right {
  text-align: right;
}

@media (max-width: 768px) {
  .labourers-grid {
    grid-template-columns: 1fr;
  }

  .labourer-stats {
    flex-direction: column;
    gap: 5px;
  }

  .summary-bar {
    flex-wrap: wrap;
  }

  .payment-header {
    grid-template-columns: 1fr;
  }
}
</style>